/**
 * "N changes since you last looked" (card 10a54f95), folded to two lines
 * (proposal 3, kanban 4d4a8707): the count and headline, then the newest
 * change; "Details ▸" lists the rest. The card stays a distinct block with
 * its count (owner ruling, review usunjl): only its list folds, so the
 * decision buttons are not pushed below a wall of history.
 *
 * UX round 2026-10-13: the count is people's changes (Merlin's note
 * rewrites are listed, not counted); when nothing changed the card says
 * when the last real activity was, amber once it is QUIET_DAYS old; and on
 * an item with no earlier open on record it still says that much.
 */
import { useState } from 'react';

import { trackEvent } from '../../services/portalTracking';
import { respondedWhen } from './respondedView';
import {
  QUIET_DAYS,
  lastActivityLine,
  lastLookLabel,
  peopleChangeCount,
  sinceHeadline,
  sinceKindLabel,
  type LastActivity,
  type SinceLastLook,
} from './sinceLastLook';
import styles from './QualityWorkspace.module.css';
import foldStyles from './IssueSummary.module.css';

export function SinceLastLookCard({
  since,
  last = null,
  threadNo,
}: {
  /** What moved since the previous open; null when no earlier open is on record. */
  since: SinceLastLook | null;
  /** The newest real activity on the item. */
  last?: LastActivity | null;
  threadNo: string;
}) {
  const [open, setOpen] = useState(false);
  const items = since?.items ?? [];
  const more = since?.more ?? false;
  const count = peopleChangeCount(items);
  const foldable = items.length > 1;
  const shown = open || !foldable ? items : items.slice(0, 1);
  const stale = count === 0 && !!last && last.days >= QUIET_DAYS;
  if (!since && !last) return null;
  return (
    <section
      className={styles.sinceCard}
      data-state={count > 0 ? 'changes' : stale ? 'stale' : 'quiet'}
      data-open={open || undefined}
      role="status"
      data-testid="since-last-look"
    >
      <div className={styles.sinceHead}>
        {count > 0 ? (
          <span className={styles.sinceCount} aria-hidden="true">
            {count}
            {more ? '+' : ''}
          </span>
        ) : stale ? (
          <span className={styles.sinceStale} aria-hidden="true">
            ⏸
          </span>
        ) : (
          <span className={styles.sinceTick} aria-hidden="true">
            ✓
          </span>
        )}
        <strong className={styles.sinceLabel}>
          {since ? sinceHeadline(count, more) : 'No earlier visit of yours on record'}
        </strong>
        {since && <span className={styles.sinceWhen}>last opened {lastLookLabel(since.since)}</span>}
        {foldable && (
          <button
            type="button"
            className={foldStyles.detailsLink}
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
              trackEvent('action', open ? 'qcp_since_last_look_collapse' : 'qcp_since_last_look_expand', {
                thread_no: threadNo,
                hidden: items.length - 1,
              });
            }}
          >
            {open ? 'Less ▴' : 'Details ▸'}
          </button>
        )}
      </div>
      {items.length > 0 && (
        <ul className={styles.sinceList}>
          {shown.map((item, i) => (
            <li
              key={`${item.at}-${i}`}
              className={`${styles.sinceItem} ${!open && foldable ? foldStyles.oneLine : ''}`}
              data-kind={item.kind}
            >
              <span className={styles.sinceKind} data-kind={item.kind}>
                {sinceKindLabel(item.kind)}
              </span>
              <span className={styles.sinceText} data-part="text">
                {item.text}
              </span>
              <span className={styles.sinceAge}>{respondedWhen(item.at)}</span>
            </li>
          ))}
        </ul>
      )}
      {count === 0 && last && (
        <p className={styles.sinceLast} data-testid="since-last-activity">
          {lastActivityLine(last)}
          {stale ? '. Nothing has moved since; chase it or close it?' : ''}
        </p>
      )}
      {more && (open || !foldable) && (
        <div className={styles.sinceFoot}>
          <span className={styles.sinceQuiet}>and older changes past what is loaded</span>
        </div>
      )}
    </section>
  );
}

export default SinceLastLookCard;
